import { ReactNode } from "react";
import { Outlet, useParams } from "react-router";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import {
  CROSS_SELL_APPLICATION_UUID_QUERY_PARAM_KEY,
  OLB_FLOW_APPLICATION_UUID_QUERY_PARAM_KEY,
} from "@lending/utils/loadPrequalApp";

const LOAN_PORTAL_FLOW = "loan_portal";

interface BankingFlowGuardProps {
  children?: ReactNode;
}

function BankingFlowGuard({ children }: BankingFlowGuardProps) {
  const { institution } = useGetInstitutionQuery();
  const { applicationUuid } = useParams<{ applicationUuid: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const appUuid =
    applicationUuid ??
    searchParams.get(OLB_FLOW_APPLICATION_UUID_QUERY_PARAM_KEY) ??
    searchParams.get(CROSS_SELL_APPLICATION_UUID_QUERY_PARAM_KEY);

  // Check if banking flow or cross-sell is enabled
  const hasBankingFlow =
    institution.lending_flows_enabled.includes("online_banking") ||
    institution.lending_flows_enabled.includes("cross_sell");
  const hasLoanPortal = institution.lending_flows_enabled.includes(LOAN_PORTAL_FLOW);

  // A UUID in the PATH means we are entering a specific application — a Loan Portal launch
  // or a resume. Access to that application is enforced by the SERVER: the application
  // endpoints scope their queryset to the principal's accessible applications (an
  // unauthorized UUID 404s) and sub-resources call validate_application_access. This client
  // guard is only institution feature-flag gating, never an authorization boundary, so it
  // need not also satisfy online_banking/cross_sell here. (Note: ExistingApplicationGuard,
  // which wraps these routes too, is a flow/step-ordering guard and short-circuits on the
  // scoped-UUID path — it does no access check.) That is what lets a standalone portal user,
  // whose institution may only have institution_site enabled, reach the OLB flow.
  //
  // Deliberately keyed on the PATH param, not on appUuid: the query-param entries
  // (token-bootstrap / cross-sell handoff) must stay gated by the institution flags below,
  // unchanged by the portal-launch relaxation. Those arrive as .../confirm-info?olb_application=…
  // with no path UUID, and their loader immediately redirects to the path-UUID route, which
  // then takes the branch above.
  if (applicationUuid && (hasBankingFlow || hasLoanPortal)) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (applicationUuid) {
    return <ErrorScreen />;
  }

  if (!hasBankingFlow) {
    return <ErrorScreen />;
  }

  // Banking-flow entry needs an application UUID (here a query-param one); without any,
  // send the user back to Online Banking to start through the proper entry point.
  if (!appUuid) {
    window.location.href = institution.lending_return_to_banking_url;
    return null;
  }

  return children ? <>{children}</> : <Outlet />;
}

export { BankingFlowGuard };
